import { type PartialDeep } from 'type-fest';

import {
  type CanvasElementFrom,
  type PartialPropsOf,
  type PropsOf,
} from '@blaze-runtime/engine-domain';

import {
  getSourceProps,
  getSourceTransforms,
} from '../../canvas-elements/element-model/transformations';

type ToggleButtonElement = CanvasElementFrom<'toggleButton'>;
type ToggleButtonProps = PartialPropsOf<'toggleButton'>;

/**
 * Packs the toggle-specific props for the transform history of a target element,
 * so a later transformation back to `toggleButton` can restore them.
 */
export function parkedToggleButtonProps(props: ToggleButtonProps): ToggleButtonProps {
  return {
    content: {
      title: props.content?.title,
      toggle: props.content?.toggle,
    },
    style: {
      states: props.style?.states,
    },
  };
}

function transformButtonToToggleButton(source: CanvasElementFrom<'button'>): ToggleButtonElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'button', 'toggleButton'>(source, 'toggleButton');
  const stateContent = {
    label: props.content?.button?.label,
    icon: props.content?.button?.icon,
    iconPosition: props.content?.button?.iconPosition,
  };
  const stateStyle = {
    label: props.style?.label,
    font: props.style?.font,
    background: {
      fill: props.style?.button?.fill,
      outline: props.style?.button?.outline,
      outlineWidth: props.style?.button?.outlineWidth,
      radius: props.style?.button?.radius,
    },
  };
  const newProps: ToggleButtonProps = {
    content: {
      // the button has no title of its own — restore one parked by a previous conversion
      title: propsT?.content?.title,
      // the single button config becomes both states; the user adjusts one afterwards
      toggle: propsT?.content?.toggle ?? {
        enabled: stateContent,
        disabled: stateContent,
      },
    },
    style: {
      button: {
        offset: props.style?.button?.offset,
      },
      states: propsT?.style?.states ?? {
        enabled: stateStyle,
        disabled: stateStyle,
      },
    },
    transforms: {
      button: {
        content: {
          button: {
            showLoader: props.content?.button?.showLoader,
          },
          action: props.content?.action,
        },
        style: {
          button: {
            visualEffect: props.style?.button?.visualEffect,
          },
        },
      } as PartialDeep<PropsOf<'button'>>,
    },
  };

  return { props: newProps } as unknown as ToggleButtonElement;
}

function transformCheckboxToToggleButton(
  source: CanvasElementFrom<'checkbox'>,
): ToggleButtonElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'checkbox', 'toggleButton'>(source, 'toggleButton');
  const newProps: ToggleButtonProps = {
    content: {
      title: { ...props.content?.title },
      toggle: propsT?.content?.toggle,
    },
    style: {
      button: {
        offset: props.style?.general?.offset,
      },
      states: propsT?.style?.states,
      general: {
        title: props.style?.general?.title,
      },
    },
    transforms: {
      checkbox: {
        content: {
          data: {
            defaultValue: props.content?.data?.defaultValue,
          },
          checkbox: {
            values: props.content?.checkbox?.values,
            preselect: props.content?.checkbox?.preselect,
            layout: props.content?.checkbox?.layout,
            readonly: props.content?.checkbox?.readonly,
            minWidth: props.content?.checkbox?.minWidth,
            maxColumns: props.content?.checkbox?.maxColumns,
          },
        },
        style: {
          general: {
            accent: props.style?.general?.accent,
          },
          checkbox: {
            label: props.style?.checkbox?.label,
            symbol: props.style?.checkbox?.symbol,
            fill: props.style?.checkbox?.fill,
            border: props.style?.checkbox?.border,
            radius: props.style?.checkbox?.radius,
          },
        },
      } as PartialDeep<PropsOf<'checkbox'>>,
    },
  };

  return { props: newProps } as unknown as ToggleButtonElement;
}

function transformCheckboxInputToToggleButton(
  source: CanvasElementFrom<'checkboxInput'>,
): ToggleButtonElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'checkboxInput', 'toggleButton'>(source, 'toggleButton');
  const newProps: ToggleButtonProps = {
    content: {
      title: { ...props.content?.title },
      toggle: propsT?.content?.toggle,
    },
    style: {
      button: {
        offset: props.style?.general?.offset,
      },
      states: propsT?.style?.states,
      general: {
        title: props.style?.general?.title,
      },
      labelBlock: props.style?.labelBlock,
    },
    transforms: {
      checkboxInput: {
        content: {
          input: {
            readonly: props.content?.input?.readonly,
          },
        },
        style: {
          fontSize: props.style?.fontSize,
          fontFamily: props.style?.fontFamily,
          lineHeight: props.style?.lineHeight,
          general: {
            accent: props.style?.general?.accent,
          },
          input: {
            symbol: props.style?.input?.symbol,
            fill: props.style?.input?.fill,
            border: props.style?.input?.border,
            radius: props.style?.input?.radius,
          },
        },
      } as PartialDeep<PropsOf<'checkboxInput'>>,
    },
  };

  return { props: newProps } as unknown as ToggleButtonElement;
}

function transformDropdownToToggleButton(
  source: CanvasElementFrom<'dropdown'>,
): ToggleButtonElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'dropdown', 'toggleButton'>(source, 'toggleButton');
  const newProps: ToggleButtonProps = {
    content: {
      title: { ...props.content?.title },
      toggle: propsT?.content?.toggle,
    },
    style: {
      button: {
        offset: props.style?.general?.offset,
      },
      states: propsT?.style?.states,
      general: {
        title: props.style?.general?.title,
      },
    },
    transforms: {
      dropdown: {
        content: {
          data: {
            defaultValue: props.content?.data?.defaultValue,
          },
          menu: {
            placeholder: props.content?.menu?.placeholder,
            values: props.content?.menu?.values,
            preselect: props.content?.menu?.preselect,
            prefix: props.content?.menu?.prefix,
            suffix: props.content?.menu?.suffix,
            dynamicSearch: props.content?.menu?.dynamicSearch,
          },
        },
        style: {
          general: {
            accent: props.style?.general?.accent,
          },
          input: {
            color: props.style?.input?.color,
            fill: props.style?.input?.fill,
            border: props.style?.input?.border,
            radius: props.style?.input?.radius,
          },
          list: props.style?.list,
        },
      } as PartialDeep<PropsOf<'dropdown'>>,
    },
  };

  return { props: newProps } as unknown as ToggleButtonElement;
}

function transformRadioToToggleButton(source: CanvasElementFrom<'radio'>): ToggleButtonElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'radio', 'toggleButton'>(source, 'toggleButton');
  const newProps: ToggleButtonProps = {
    content: {
      title: { ...props.content?.title },
      toggle: propsT?.content?.toggle,
    },
    style: {
      states: propsT?.style?.states,
      general: {
        title: props.style?.general?.title,
      },
    },
    transforms: {
      radio: {
        content: {
          data: {
            defaultValue: props.content?.data?.defaultValue,
          },
          radio: {
            values: props.content?.radio?.values,
            preselect: props.content?.radio?.preselect,
            layout: props.content?.radio?.layout,
            readonly: props.content?.radio?.readonly,
          },
        },
        style: {
          fontSize: props.style?.fontSize,
          general: {
            accent: props.style?.general?.accent,
          },
          radio: {
            label: props.style?.radio?.label,
            fill: props.style?.radio?.fill,
            border: props.style?.radio?.border,
          },
        },
      } as PartialDeep<PropsOf<'radio'>>,
    },
  };

  return { props: newProps } as unknown as ToggleButtonElement;
}

function transformTagsToToggleButton(source: CanvasElementFrom<'tags'>): ToggleButtonElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'tags', 'toggleButton'>(source, 'toggleButton');
  const newProps: ToggleButtonProps = {
    content: {
      title: { ...props.content?.title },
      toggle: propsT?.content?.toggle,
    },
    style: {
      states: propsT?.style?.states,
      general: {
        title: props.style?.general?.title,
      },
    },
    transforms: {
      tags: {
        content: {
          tags: {
            placeholder: props.content?.tags?.placeholder,
            values: props.content?.tags?.values,
            preselect: props.content?.tags?.preselect,
          },
          options: {
            canAddNew: props.content?.options?.canAddNew,
            readonly: props.content?.options?.readonly,
            singleSelect: props.content?.options?.singleSelect,
          },
        },
        style: {
          fontSize: props.style?.fontSize,
          general: {
            accent: props.style?.general?.accent,
          },
          input: {
            color: props.style?.input?.color,
            fill: props.style?.input?.fill,
            border: props.style?.input?.border,
            radius: props.style?.input?.radius,
          },
        },
      } as PartialDeep<PropsOf<'tags'>>,
    },
  };

  return { props: newProps } as unknown as ToggleButtonElement;
}

export const transformToToggleButton = {
  transformButtonToToggleButton,
  transformCheckboxInputToToggleButton,
  transformCheckboxToToggleButton,
  transformDropdownToToggleButton,
  transformRadioToToggleButton,
  transformTagsToToggleButton,
};
